const API_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve image URLs - handles both absolute and relative (backend) URLs
export function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) return url;
  if (url.startsWith('/api/')) return `${API_URL}${url}`;
  return url;
}

class ApiService {
  constructor() {
    this.baseUrl = `${API_URL}/api`;
  }

  getToken() {
    return localStorage.getItem('gopiko_token');
  }

  setToken(token) {
    localStorage.setItem('gopiko_token', token);
  }

  clearToken() {
    localStorage.removeItem('gopiko_token');
    localStorage.removeItem('gopiko_user');
  }

  getStoredUser() {
    const user = localStorage.getItem('gopiko_user');
    return user ? JSON.parse(user) : null;
  }

  setStoredUser(user) {
    localStorage.setItem('gopiko_user', JSON.stringify(user));
  }

  async request(endpoint, options = {}) {
    const headers = { ...options.headers };
    if (!options.isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const { isFormData, ...fetchOptions } = options;
    let response;
    try {
      response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...fetchOptions,
        headers,
      });
    } catch (networkErr) {
      throw new Error('Network error — check your connection and try again');
    }

    if (response.status === 401) {
      this.clearToken();
      window.dispatchEvent(new Event('auth:logout'));
    }

    // Handle non-JSON responses (e.g. proxy/ingress HTML error pages)
    const contentType = response.headers.get('content-type') || '';
    let data;
    try {
      data = await response.json();
    } catch {
      if (response.status === 413) throw new Error('File too large for upload. Try a smaller file.');
      if (!response.ok) throw new Error(`Server error (${response.status}). Please try again.`);
      return {};
    }

    if (!response.ok) {
      const msg = typeof data.detail === 'string' ? data.detail : Array.isArray(data.detail) ? data.detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Something went wrong';
      throw new Error(msg);
    }
    return data;
  }

  // Auth
  async signup(name, email, password, phone, recaptchaToken, referralCode) {
    const data = await this.request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ name, email, password, phone, recaptcha_token: recaptchaToken, referral_code: referralCode || null }),
    });
    // Signup no longer returns token — user must verify email first
    return data;
  }

  async login(email, password, recaptchaToken) {
    const data = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, recaptcha_token: recaptchaToken }),
    });
    // Only set token if login succeeded (email verified)
    if (data.token) {
      this.setToken(data.token);
      this.setStoredUser(data.user);
    }
    return data;
  }

  // Google OAuth
  async googleCallback(sessionId) {
    const data = await this.request('/auth/google/callback', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    });
    this.setToken(data.token);
    this.setStoredUser(data.user);
    return data;
  }

  async getMe() {
    return this.request('/auth/me');
  }

  logout() {
    this.clearToken();
  }

  // Listings
  async getListings(params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, value);
      }
    });
    return this.request(`/listings?${query.toString()}`);
  }

  async getListing(id) {
    return this.request(`/listings/${id}`);
  }

  async createListing(data) {
    return this.request('/listings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateListing(id, data) {
    return this.request(`/listings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteListing(id) {
    return this.request(`/listings/${id}`, { method: 'DELETE' });
  }

  async getMyListings() {
    return this.request('/listings/user/mine');
  }

  async getRelatedListings(listingId, limit = 8) {
    return this.request(`/listings/${listingId}/related?limit=${limit}`);
  }

  async getExpiringListings() {
    return this.request('/listings/user/expiring');
  }

  async toggleFavorite(listingId) {
    return this.request(`/listings/${listingId}/favorite`, { method: 'POST' });
  }

  async getFavorites() {
    return this.request('/listings/user/favorites');
  }

  async getFavoriteStatus(listingId) {
    return this.request(`/listings/${listingId}/favorite-status`);
  }

  async trackShare(listingId) {
    return this.request(`/listings/${listingId}/share`, { method: 'POST' });
  }

  // Saved Searches
  async getSavedSearches(category) {
    const query = category ? `?category=${category}` : '';
    return this.request(`/saved-searches${query}`);
  }

  async createSavedSearch(data) {
    return this.request('/saved-searches', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteSavedSearch(id) {
    return this.request(`/saved-searches/${id}`, { method: 'DELETE' });
  }
  async updateSavedSearch(id, data) {
    return this.request(`/saved-searches/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  // Geo-Location
  async detectLocation() {
    return this.request('/geo/detect');
  }

  // Image Upload
  async uploadImage(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const headers = {};
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Upload failed');
    }
    return data;
  }

  /**
   * Upload image with real progress tracking via XMLHttpRequest.
   * @param {File} file
   * @param {(pct: number) => void} onProgress — 0-100
   * @returns {Promise<{url: string}>}
   */
  uploadImageWithProgress(file, onProgress, _retries = 0) {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.baseUrl}/upload`);
      const token = this.getToken();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.timeout = 60000; // 60 second timeout
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else {
            reject(new Error(data.detail || 'Upload failed'));
          }
        } catch { reject(new Error('Upload failed')); }
      };
      xhr.onerror = () => {
        if (_retries < 2) {
          // Retry once on network error
          setTimeout(() => {
            this.uploadImageWithProgress(file, onProgress, _retries + 1).then(resolve).catch(reject);
          }, 1000);
        } else {
          reject(new Error('Network error — check your connection and try again'));
        }
      };
      xhr.ontimeout = () => {
        if (_retries < 1) {
          setTimeout(() => {
            this.uploadImageWithProgress(file, onProgress, _retries + 1).then(resolve).catch(reject);
          }, 1000);
        } else {
          reject(new Error('Upload timed out — try a smaller image'));
        }
      };
      xhr.send(formData);
    });
  }

  async uploadMultipleImages(files) {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    
    const headers = {};
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}/upload/multiple`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || 'Upload failed');
    }
    return data;
  }

  // Locations
  async getCities(country, state) {
    const query = new URLSearchParams({ country, state });
    return this.request(`/locations/cities?${query.toString()}`);
  }

  async getCountryData(country) {
    return this.request(`/locations/country-data?country=${encodeURIComponent(country)}`);
  }

  async searchCities(query, country = null, limit = 20) {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    if (country) params.set('country', country);
    return this.request(`/locations/search?${params.toString()}`);
  }

  async postcodeLookup(code, country = '') {
    const params = new URLSearchParams({ code });
    if (country) params.set('country', country);
    return this.request(`/locations/postcode-lookup?${params.toString()}`);
  }

  async geocodeCity(city, state = '', country = '') {
    const params = new URLSearchParams({ city });
    if (state) params.set('state', state);
    if (country) params.set('country', country);
    return this.request(`/locations/geocode?${params.toString()}`);
  }

  // Trending
  async getTrendingSearches(country = '') {
    const params = new URLSearchParams();
    if (country) params.set('country', country);
    return this.request(`/trending/searches?${params.toString()}`);
  }

  // Profile & Password
  async updateProfile(data) {
    return this.request('/auth/profile', { method: 'PUT', body: JSON.stringify(data) });
  }
  async changePassword(data) {
    return this.request('/auth/change-password', { method: 'PUT', body: JSON.stringify(data) });
  }

  async forgotPassword(email) {
    return this.request('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) });
  }

  async resetPassword(token, newPassword) {
    return this.request('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, new_password: newPassword }) });
  }

  // Seller Badges & Profile
  async getUserBadges(userId) {
    return this.request(`/users/${userId}/badges`);
  }
  async getUserTrustScore(userId) {
    return this.request(`/users/${userId}/trust-score`);
  }
  async batchPrices(listingIds) {
    return this.request('/listings/batch-prices', { method: 'POST', body: JSON.stringify({ listing_ids: listingIds }) });
  }
  async getUserProfile(userId) {
    return this.request(`/users/${userId}/profile`);
  }

  // Verification
  async sendVerificationCode() {
    return this.request('/verification/send-code', { method: 'POST' });
  }
  async verifyEmailCode(code) {
    return this.request('/verification/verify-code', { method: 'POST', body: JSON.stringify({ code }) });
  }
  async uploadVerificationId(file) {
    const formData = new FormData();
    formData.append('file', file);
    const headers = {};
    const token = this.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${this.baseUrl}/verification/upload-id`, {
      method: 'POST',
      headers,
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Upload failed');
    return data;
  }
  async submitVerification() {
    return this.request('/verification/submit', { method: 'POST' });
  }
  async getVerificationStatus() {
    return this.request('/verification/status');
  }
  async getAdminVerificationRequests(status = 'pending') {
    return this.request(`/verification/admin/requests?status=${status}`);
  }
  async approveVerification(requestId) {
    return this.request(`/verification/admin/requests/${requestId}/approve`, { method: 'PUT' });
  }
  async rejectVerification(requestId, reason = '') {
    return this.request(`/verification/admin/requests/${requestId}/reject`, { method: 'PUT', body: JSON.stringify({ reason }) });
  }
  async bulkVerificationAction(requestIds, action, reason = '') {
    return this.request('/verification/admin/bulk-action', {
      method: 'POST',
      body: JSON.stringify({ request_ids: requestIds, action, reason }),
    });
  }

  // AI Suggestions
  async getAISuggestion(title, description = '', location = '', country = '') {
    return this.request('/ai/suggest', {
      method: 'POST',
      body: JSON.stringify({ title, description, location, country }),
    });
  }

  // Job Alerts
  async createJobAlert(data) {
    return this.request('/job-alerts', { method: 'POST', body: JSON.stringify(data) });
  }
  async getJobAlerts() {
    return this.request('/job-alerts');
  }
  async deleteJobAlert(alertId) {
    return this.request(`/job-alerts/${alertId}`, { method: 'DELETE' });
  }

  // Messages / Chat
  async sendMessage(data) {
    return this.request('/messages', { method: 'POST', body: JSON.stringify(data) });
  }
  async uploadChatAttachment(file) {
    const formData = new FormData();
    formData.append('file', file);
    const token = typeof window !== 'undefined' ? localStorage.getItem('gopiko_token') : null;
    const response = await fetch(`${this.baseUrl}/messages/upload-attachment`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Upload failed');
    }
    return response.json();
  }
  async sendInquiry(data) {
    return this.request('/messages/inquiry', { method: 'POST', body: JSON.stringify(data) });
  }
  async getConversations(includeArchived = false) {
    const params = includeArchived ? '?include_archived=true' : '';
    return this.request(`/messages/conversations${params}`);
  }
  async getMessages(otherUserId) {
    return this.request(`/messages/${otherUserId}`);
  }
  async getUnreadCount() {
    return this.request('/messages/unread/count');
  }

  // Reports
  async createReport(data) {
    return this.request('/reports', { method: 'POST', body: JSON.stringify(data) });
  }

  // Translation
  async translateMessage(text, targetLanguage) {
    return this.request('/messages/translate', {
      method: 'POST',
      body: JSON.stringify({ text, target_language: targetLanguage }),
    });
  }
  async detectLanguage(texts) {
    return this.request('/messages/detect-language', {
      method: 'POST',
      body: JSON.stringify({ texts }),
    });
  }

  // Admin
  async getAdminStats() {
    return this.request('/admin/stats');
  }

  // Admin Moderation Queue
  async getModerationQueue(status = 'flagged', page = 1, limit = 20) {
    return this.request(`/admin/moderation/queue?status=${status}&page=${page}&limit=${limit}`);
  }
  async getModerationStats() {
    return this.request('/admin/moderation/stats');
  }
  async approveListing(listingId) {
    return this.request(`/admin/moderation/${listingId}/approve`, { method: 'PATCH' });
  }
  async rejectListing(listingId, reason = '') {
    return this.request(`/admin/moderation/${listingId}/reject`, {
      method: 'PATCH',
      body: JSON.stringify({ reason }),
    });
  }

  async getAdminPricing() {
    return this.request('/admin/pricing');
  }
  async updateDealerPricing(packages) {
    return this.request('/admin/pricing/dealer', { method: 'PUT', body: JSON.stringify(packages) });
  }
  async updatePremiumPricing(packages) {
    return this.request('/admin/pricing/premium', { method: 'PUT', body: JSON.stringify(packages) });
  }
  async updateJobTierPricing(tiers) {
    return this.request('/admin/pricing/job-tiers', { method: 'PUT', body: JSON.stringify(tiers) });
  }
  async updateBundlePricing(bundles) {
    return this.request('/admin/pricing/bundles', { method: 'PUT', body: JSON.stringify(bundles) });
  }
  async getCountryStats() {
    return this.request('/admin/country-stats');
  }
  async getCountryDetail(country) {
    return this.request(`/admin/country-detail/${encodeURIComponent(country)}`);
  }
  async getAdminUsers(page = 1, search = '', status = '') {
    const params = new URLSearchParams({ page, page_size: 200 });
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    return this.request(`/admin/users?${params}`);
  }
  async getAdminUser(userId) {
    return this.request(`/admin/users/${userId}`);
  }
  async toggleBanUser(userId) {
    return this.request(`/admin/users/${userId}/ban`, { method: 'PUT' });
  }
  async adminDeleteUser(userId) {
    return this.request(`/admin/users/${userId}`, { method: 'DELETE' });
  }
  async setUserRole(userId, role) {
    return this.request(`/admin/users/${userId}/role?role=${role}`, { method: 'PUT' });
  }
  async toggleVerifyUser(userId) {
    return this.request(`/admin/users/${userId}/verify`, { method: 'PUT' });
  }
  async getAdminListings(page = 1, { status, category, search, featured, country } = {}) {
    const params = new URLSearchParams({ page, page_size: 200 });
    if (status) params.append('status', status);
    if (category) params.append('category', category);
    if (search) params.append('search', search);
    if (featured !== undefined) params.append('featured', featured);
    if (country) params.append('country', country);
    return this.request(`/admin/listings?${params}`);
  }
  async updateListingStatus(listingId, status) {
    return this.request(`/admin/listings/${listingId}/status?status=${status}`, { method: 'PUT' });
  }
  async toggleFeatureListing(listingId) {
    return this.request(`/admin/listings/${listingId}/feature`, { method: 'PUT' });
  }
  async adminDeleteListing(listingId) {
    return this.request(`/admin/listings/${listingId}`, { method: 'DELETE' });
  }
  async getAdminMessages(page = 1, search = '') {
    const params = new URLSearchParams({ page, page_size: 100 });
    if (search) params.append('search', search);
    return this.request(`/admin/messages?${params}`);
  }
  async adminDeleteMessage(messageId) {
    return this.request(`/admin/messages/${messageId}`, { method: 'DELETE' });
  }
  async adminBulkDeleteMessages(ids) {
    return this.request('/admin/messages/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) });
  }
  async adminReplyMessage(originalMessageId, text, attachments = []) {
    return this.request('/admin/messages/reply', { method: 'POST', body: JSON.stringify({ original_message_id: originalMessageId, text, attachments }) });
  }
  async adminUploadMessageAttachment(file) {
    const formData = new FormData();
    formData.append('file', file);
    const token = this.getToken();
    const res = await fetch(`${this.baseUrl}/admin/messages/upload`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData
    });
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Upload failed'); }
    return res.json();
  }
  async getAdminReports(status = '') {
    return this.request(`/admin/reports${status ? `?status=${status}` : ''}`);
  }
  async resolveReport(reportId) {
    return this.request(`/admin/reports/${reportId}/resolve`, { method: 'PUT' });
  }
  async getAdminCategories() {
    return this.request('/admin/categories');
  }
  async createAdminCategory(data) {
    return this.request('/admin/categories', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateAdminCategory(slug, data) {
    return this.request(`/admin/categories/${slug}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteAdminCategory(slug) {
    return this.request(`/admin/categories/${slug}`, { method: 'DELETE' });
  }
  async getAdminSettings() {
    return this.request('/admin/settings');
  }
  async updateAdminSettings(data) {
    return this.request('/admin/settings', { method: 'PUT', body: JSON.stringify(data) });
  }
  async getAdminContent() {
    return this.request('/admin/content');
  }
  async updateAdminContent(data) {
    return this.request('/admin/content', { method: 'PUT', body: JSON.stringify(data) });
  }
  async getPublicContent() {
    return this.request('/admin/content/public');
  }

  // Categories
  async getCategories() {
    return this.request('/categories');
  }

  async getCategory(slug) {
    return this.request(`/categories/${slug}`);
  }

  // Notifications
  async getNotifications(page = 1) {
    return this.request(`/notifications?page=${page}`);
  }

  async getUnreadNotificationCount() {
    return this.request('/notifications/count');
  }

  async markNotificationRead(id) {
    return this.request(`/notifications/${id}/read`, { method: 'PUT' });
  }

  async markAllNotificationsRead() {
    return this.request('/notifications/read-all', { method: 'PUT' });
  }

  async getNotificationPreferences() {
    return this.request('/notifications/preferences');
  }

  async updateNotificationPreferences(prefs) {
    return this.request('/notifications/preferences', { method: 'PUT', body: JSON.stringify(prefs) });
  }

  // Analytics
  async trackListingView(listingId) {
    return this.request(`/analytics/listings/${listingId}/view`, { method: 'POST' });
  }

  async getListingAnalytics(listingId) {
    return this.request(`/analytics/listings/${listingId}`);
  }

  async getMyAnalytics() {
    return this.request('/analytics/my-listings');
  }

  async getDashboardCharts() {
    return this.request('/analytics/dashboard-charts');
  }

  async getSellerPerformance() {
    return this.request('/analytics/seller-performance');
  }

  async semanticSearch(query) {
    return this.request('/ai/semantic-search', {
      method: 'POST', body: JSON.stringify({ query }),
    });
  }

  async getRecommendationFeed(limit = 12, skip = 0, country = '') {
    const params = new URLSearchParams({ limit, skip });
    if (country) params.append('country', country);
    return this.request(`/recommendations/feed?${params.toString()}`);
  }

  // Buyer Intent Alerts
  async getBuyerIntentAlerts(page = 1, unreadOnly = false) {
    return this.request(`/buyer-intent/alerts?page=${page}&unread_only=${unreadOnly}`);
  }

  async getBuyerIntentSummary() {
    return this.request('/buyer-intent/summary');
  }

  async markIntentAlertRead(alertId) {
    return this.request(`/buyer-intent/alerts/${alertId}/read`, { method: 'PUT' });
  }

  async markAllIntentAlertsRead() {
    return this.request('/buyer-intent/alerts/read-all', { method: 'PUT' });
  }

  // Bulk Import
  async bulkImportListings(file, mode = 'create') {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('gopiko_token');
    const res = await fetch(`${this.baseUrl}/dealers/bulk/import?mode=${mode}`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Import failed');
    }
    return res.json();
  }

  async getBulkImportHistory(page = 1) {
    return this.request(`/dealers/bulk/import-history?page=${page}`);
  }

  async getInventoryAnalytics() {
    return this.request('/dealers/bulk/inventory-analytics');
  }

  async exportInventory(format = 'csv') {
    const token = localStorage.getItem('gopiko_token');
    const res = await fetch(`${this.baseUrl}/dealers/bulk/export?format=${format}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Export failed');
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gopiko_inventory.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async getBulkSampleJson() {
    return this.request('/dealers/bulk/sample-json');
  }

  // Feed Scheduler
  async getFeedSchedules() {
    return this.request('/dealers/bulk/schedule');
  }

  async createFeedSchedule(data) {
    return this.request('/dealers/bulk/schedule', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateFeedSchedule(id, data) {
    return this.request(`/dealers/bulk/schedule/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteFeedSchedule(id) {
    return this.request(`/dealers/bulk/schedule/${id}`, { method: 'DELETE' });
  }

  async triggerFeedSync(id) {
    return this.request(`/dealers/bulk/schedule/${id}/sync-now`, { method: 'POST' });
  }

  // Buyer Intent Follow-Up
  async sendIntentFollowUp(alertId) {
    return this.request(`/buyer-intent/follow-up/${alertId}`, { method: 'POST' });
  }

  // Hot Listings
  async getHotListings(limit = 10) {
    return this.request(`/hot-listings?limit=${limit}`);
  }

  async toggleSavedSearchAlerts(id) {
    return this.request(`/saved-searches/${id}/alerts`, { method: 'PUT' });
  }

  // Profile
  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('gopiko_token');
    const res = await fetch(`${this.baseUrl}/auth/avatar`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) throw new Error('Upload failed');
    return res.json();
  }

  // Payments
  async getPremiumPackages() {
    return this.request('/payments/packages');
  }

  async createCheckout(data) {
    // Accept object: { listing_id, package_id, origin_url }
    const body = typeof data === 'object' && data.listing_id
      ? data
      : { listing_id: data, package_id: arguments[1], origin_url: window.location.origin };
    return this.request('/payments/checkout', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async createCheckoutDirect(packageId) {
    return this.request('/payments/checkout-direct', {
      method: 'POST',
      body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin }),
    });
  }

  async getPaymentStatus(sessionId) {
    return this.request(`/payments/status/${sessionId}`);
  }

  async getMyTransactions() {
    return this.request('/payments/my-transactions');
  }

  // Dealer Checkout
  async createDealerCheckout(data) {
    return this.request('/dealers/checkout', {
      method: 'POST',
      body: JSON.stringify({ ...data, origin_url: window.location.origin }),
    });
  }

  async getDealerPaymentStatus(sessionId) {
    return this.request(`/dealers/payment-status/${sessionId}`);
  }

  async renewDealerSubscription() {
    return this.request('/dealers/renew', { method: 'POST' });
  }

  async uploadDealerBranding(file, field) {
    const formData = new FormData();
    formData.append('file', file);
    const token = this.getToken();
    const res = await fetch(`${this.baseUrl}/dealers/upload-branding?field=${field}`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Upload failed'); }
    return res.json();
  }

  // Premium Listings
  async getPremiumListings(limit = 12, country = '', category = '') {
    const params = new URLSearchParams({ limit });
    if (country) params.append('country', country);
    if (category) params.append('category', category);
    return this.request(`/listings/premium?${params.toString()}`);
  }

  async getListingCounts(country) {
    const params = country ? `?country=${encodeURIComponent(country)}` : '';
    return this.request(`/listings/counts${params}`);
  }

  // Reviews
  async createReview(data) {
    return this.request('/reviews', { method: 'POST', body: JSON.stringify(data) });
  }

  async getListingReviews(listingId, page = 1) {
    return this.request(`/reviews/listing/${listingId}?page=${page}`);
  }

  async getSellerReviews(sellerId, page = 1) {
    return this.request(`/reviews/seller/${sellerId}?page=${page}`);
  }

  async getSellerStats(sellerId) {
    return this.request(`/reviews/seller-stats/${sellerId}`);
  }

  async getMyReceivedReviews() {
    return this.request('/reviews/my-received');
  }

  // Listing Expiry
  async renewListing(listingId) {
    return this.request(`/listings/${listingId}/renew`, { method: 'POST' });
  }

  async quickRelist(listingId) {
    return this.request(`/listings/${listingId}/quick-relist`, { method: 'POST' });
  }

  async bulkRelist(listingIds) {
    return this.request('/listings/bulk-relist', {
      method: 'POST',
      body: JSON.stringify({ listing_ids: listingIds }),
    });
  }



  async toggleAutoRenew(listingId) {
    return this.request(`/listings/${listingId}/auto-renew`, { method: 'PUT' });
  }

  // Bulk Actions
  async bulkDeleteListings(listingIds) {
    return this.request('/listings/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ listing_ids: listingIds }),
    });
  }

  async bulkRenewListings(listingIds) {
    return this.request('/listings/bulk-renew', {
      method: 'POST',
      body: JSON.stringify({ listing_ids: listingIds }),
    });
  }

  async getListingExpiry(listingId) {
    return this.request(`/listings/${listingId}/expiry`);
  }

  // PayPal
  async getPayPalClientId() {
    return this.request('/paypal/client-id');
  }

  async createPayPalOrder(listingId, packageId) {
    return this.request('/paypal/create-order', {
      method: 'POST',
      body: JSON.stringify({ listing_id: listingId, package_id: packageId, origin_url: window.location.origin }),
    });
  }

  async capturePayPalOrder(orderId) {
    return this.request(`/paypal/capture-order/${orderId}`, { method: 'POST' });
  }

  async getPayPalOrderStatus(orderId) {
    return this.request(`/paypal/order-status/${orderId}`);
  }

  async translateText(text, targetLang, sourceLang = 'en') {
    return this.request('/translate', {
      method: 'POST',
      body: JSON.stringify({ text, target_lang: targetLang, source_lang: sourceLang }),
    });
  }

  // ---- Email Marketing ----
  async subscribeNewsletter(email, name = '') {
    return this.request('/email-marketing/subscribe', {
      method: 'POST',
      body: JSON.stringify({ email, name }),
    });
  }

  async unsubscribeNewsletter(email) {
    return this.request('/email-marketing/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async getEmailSubscribers(page = 1, pageSize = 50, search = '') {
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (search) params.append('search', search);
    return this.request(`/email-marketing/admin/subscribers?${params}`);
  }

  async deleteEmailSubscriber(subscriberId) {
    return this.request(`/email-marketing/admin/subscribers/${subscriberId}`, { method: 'DELETE' });
  }

  async getEmailTemplates() {
    return this.request('/email-marketing/admin/templates');
  }

  async createEmailTemplate(data) {
    return this.request('/email-marketing/admin/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEmailTemplate(templateId, data) {
    return this.request(`/email-marketing/admin/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteEmailTemplate(templateId) {
    return this.request(`/email-marketing/admin/templates/${templateId}`, { method: 'DELETE' });
  }

  async getEmailCampaigns(page = 1, pageSize = 20) {
    return this.request(`/email-marketing/admin/campaigns?page=${page}&page_size=${pageSize}`);
  }

  async getEmailCampaign(campaignId) {
    return this.request(`/email-marketing/admin/campaigns/${campaignId}`);
  }

  async sendEmailCampaign(data) {
    return this.request('/email-marketing/admin/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteEmailCampaign(campaignId) {
    return this.request(`/email-marketing/admin/campaigns/${campaignId}`, { method: 'DELETE' });
  }

  async cancelEmailCampaign(campaignId) {
    return this.request(`/email-marketing/admin/campaigns/${campaignId}/cancel`, { method: 'POST' });
  }

  async pickAbWinner(campaignId, variant = 'auto') {
    return this.request(`/email-marketing/admin/campaigns/${campaignId}/pick-winner?variant=${variant}`, { method: 'POST' });
  }

  async getEmailMarketingStats() {
    return this.request('/email-marketing/admin/stats');
  }

  async getExchangeRates() {
    return this.request('/exchange-rates');
  }

  async detectCountry() {
    return this.request('/geo/detect');
  }

  async getAvailableCountries() {
    return this.request('/geo/countries');
  }

  // ---- Job Board Tier APIs ----

  async getJobTiers() {
    return this.request('/job-tiers');
  }

  async createJobTierCheckout(data) {
    return this.request('/payments/job-checkout', {
      method: 'POST',
      body: JSON.stringify({ ...data, origin_url: window.location.origin }),
    });
  }

  async getJobTierPaymentStatus(sessionId) {
    return this.request(`/payments/job-status/${sessionId}`);
  }

  // ---- Vehicle Tier APIs ----

  async getVehicleTiers() {
    return this.request('/vehicle-tiers');
  }

  async createVehicleTierCheckout(data) {
    return this.request('/payments/vehicle-checkout', {
      method: 'POST',
      body: JSON.stringify({ ...data, origin_url: window.location.origin }),
    });
  }

  async getVehicleTierPaymentStatus(sessionId) {
    return this.request(`/payments/vehicle-status/${sessionId}`);
  }

  // ---- Real Estate Tier APIs ----

  async getRealEstateTiers() {
    return this.request('/real-estate-tiers');
  }

  async createRealEstateTierCheckout(data) {
    return this.request('/payments/real-estate-checkout', {
      method: 'POST',
      body: JSON.stringify({ ...data, origin_url: window.location.origin }),
    });
  }

  async getRealEstateTierPaymentStatus(sessionId) {
    return this.request(`/payments/real-estate-status/${sessionId}`);
  }

  // ---- Machinery Tier APIs ----

  async getMachineryTiers() {
    return this.request('/machinery-tiers');
  }

  async createMachineryTierCheckout(data) {
    return this.request('/payments/machinery-checkout', {
      method: 'POST',
      body: JSON.stringify({ ...data, origin_url: window.location.origin }),
    });
  }

  async getMachineryTierPaymentStatus(sessionId) {
    return this.request(`/payments/machinery-status/${sessionId}`);
  }

  // ---- AI Listing Assistant APIs ----

  async analyzeListingImages(data) {
    return this.request('/ai/analyze-listing', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getSmartPricing(data) {
    return this.request('/ai/smart-pricing', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getCategoryDashboard(category) {
    return this.request(`/category-dashboard/${category}`);
  }

  async getPreferences() {
    return this.request('/auth/preferences');
  }

  async updatePreferences(prefs) {
    return this.request('/auth/preferences', {
      method: 'PUT',
      body: JSON.stringify(prefs),
    });
  }

  async applyToJob(listingId, data) {
    return this.request(`/listings/${listingId}/apply`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async uploadCV(formData) {
    const token = this.getToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${this.baseUrl}/upload/cv`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'CV upload failed');
    }
    return res.json();
  }

  async analyzeCv(cvUrl, jobListingId = null) {
    return this.request('/cv/analyze', {
      method: 'POST',
      body: JSON.stringify({ cv_url: cvUrl, job_listing_id: jobListingId }),
    });
  }

  async getMyCvAnalyses() {
    return this.request('/cv/my-analyses');
  }

  // Job Alerts
  async getMyJobAlerts() {
    return this.request('/job-alerts');
  }
  async createJobAlert(data) {
    return this.request('/job-alerts', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateJobAlert(alertId, data) {
    return this.request(`/job-alerts/${alertId}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteJobAlert(alertId) {
    return this.request(`/job-alerts/${alertId}`, { method: 'DELETE' });
  }

  async getJobApplications(listingId) {
    return this.request(`/listings/${listingId}/applications`);
  }

  async getMyApplications() {
    return this.request('/my-applications');
  }

  async updateApplicationStatus(listingId, appId, status) {
    return this.request(`/listings/${listingId}/applications/${appId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async bumpListing(listingId) {
    return this.request(`/listings/${listingId}/bump`, { method: 'POST' });
  }

  // ---- Bulk Hiring Bundle APIs ----

  async getBundleConfig() {
    return this.request('/payments/bundles');
  }

  async getMyBundle() {
    return this.request('/payments/my-bundle');
  }

  async createBundleCheckout(bundleKey) {
    return this.request('/payments/bundle-checkout', {
      method: 'POST',
      body: JSON.stringify({ bundle_key: bundleKey, origin_url: window.location.origin }),
    });
  }

  async getBundlePaymentStatus(sessionId) {
    return this.request(`/payments/bundle-status/${sessionId}`);
  }

  async useBundleCredit(listingId) {
    return this.request('/payments/use-bundle-credit', {
      method: 'POST',
      body: JSON.stringify({ listing_id: listingId }),
    });
  }

  async useUrgentUpgrade(listingId) {
    return this.request('/payments/use-urgent-upgrade', {
      method: 'POST',
      body: JSON.stringify({ listing_id: listingId }),
    });
  }

  async getEmployerDashboard() {
    return this.request('/employer/dashboard');
  }

  async getJobProfile() {
    return this.request('/job-profile');
  }

  async updateJobProfile(data) {
    return this.request('/job-profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getPublicJobProfile(userId) {
    return this.request(`/job-profile/${userId}/public`);
  }

  // Drafts
  async saveDraft(draftData) {
    return this.request('/drafts', {
      method: 'POST',
      body: JSON.stringify(draftData),
    });
  }

  async getMyDraft() {
    return this.request('/drafts');
  }

  async deleteMyDraft() {
    return this.request('/drafts', { method: 'DELETE' });
  }

  // Listing Templates
  async saveTemplate(templateData) {
    return this.request('/templates', {
      method: 'POST',
      body: JSON.stringify(templateData),
    });
  }

  async getTemplates() {
    return this.request('/templates');
  }

  async deleteTemplate(templateId) {
    return this.request(`/templates/${templateId}`, { method: 'DELETE' });
  }

  // Content Moderation
  async moderateText(text) {
    return this.request('/moderate/text', {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  async moderateImage(imageUrl) {
    return this.request('/moderate/image', {
      method: 'POST',
      body: JSON.stringify({ image_url: imageUrl }),
    });
  }

  async moderateImages(imageUrls) {
    return this.request('/moderate/images', {
      method: 'POST',
      body: JSON.stringify({ image_urls: imageUrls }),
    });
  }

  // ── Blog ──
  async getBlogArticles(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/blog/articles?${query}`);
  }

  async getBlogArticle(slug) {
    return this.request(`/blog/articles/${encodeURIComponent(slug)}`);
  }

  async getBlogFeatured() {
    return this.request('/blog/featured');
  }

  async getBlogCategories() {
    return this.request('/blog/categories');
  }

  async getBlogRefreshStatus() {
    return this.request('/blog/refresh-status');
  }

  async triggerBlogRefresh() {
    return this.request('/blog/refresh', { method: 'POST' });
  }
}

const api = new ApiService();
export default api;
